import React, { useState, useEffect, useRef } from "react";

/**
 * NewClientModal — create a client, then hand off to Quick Start.
 *
 * Lived inside CoordinatorClients until the agency dashboard needed it too.
 * The admin holds CAP_CREATE_CLIENT (every account owner does) and could
 * already create clients through the API; the dashboard button was just
 * disabled with "Coming soon". One component, two doors, same result.
 *
 * Posts to /api/clients, which is gated server-side on create_client — this
 * component never decides who may create, only how.
 */

// ── New client modal ───────────────────────────────────────────────────────

export default function NewClientModal({ onClose, onCreate }) {
  const [form, setForm] = useState({ display_name: "", full_name: "", pronouns: "", location_display: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);
  const firstRef = useRef();

  useEffect(() => { firstRef.current?.focus(); }, []);

  function handleKey(e) { if (e.key === "Escape") onClose(); }

  function set(key, val) { setForm(prev => ({ ...prev, [key]: val })); }

  async function handleSubmit() {
    if (!form.display_name.trim()) { setError("Display name is required."); return; }
    setSaving(true);
    setError(null);
    try {
      const res  = await fetch("/api/clients", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          display_name:     form.display_name.trim(),
          full_name:        form.full_name.trim() || form.display_name.trim(),
          pronouns:         form.pronouns.trim() || null,
          location_display: form.location_display.trim() || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || "Create failed"); setSaving(false); return; }
      onCreate(data.id);
    } catch (e) { setError("Network error — please try again."); setSaving(false); }
  }

  return (
    <div style={modal.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={modal.box} onKeyDown={handleKey}>
        <div style={modal.header}>
          <h2 style={modal.title}>New client</h2>
          <button style={modal.close} onClick={onClose}>×</button>
        </div>

        {error && <p style={modal.error}>{error}</p>}

        <label style={modal.label}>
          Display name <span style={modal.req}>required</span>
          <input ref={firstRef} style={modal.input} value={form.display_name}
            onChange={e => set("display_name", e.target.value)}
            placeholder="Maris-demo"
            onKeyDown={e => e.key === "Enter" && handleSubmit()} />
        </label>

        <label style={modal.label}>
          Full legal name
          <input style={modal.input} value={form.full_name}
            onChange={e => set("full_name", e.target.value)}
            placeholder="Defaults to display name if blank" />
        </label>

        <div style={modal.row}>
          <label style={{ ...modal.label, flex: 1 }}>
            Pronouns
            <input style={modal.input} value={form.pronouns}
              onChange={e => set("pronouns", e.target.value)}
              placeholder="she/her" />
          </label>
          <label style={{ ...modal.label, flex: 1 }}>
            Location
            <input style={modal.input} value={form.location_display}
              onChange={e => set("location_display", e.target.value)}
              placeholder="Sonoma County, CA" />
          </label>
        </div>

        <div style={modal.footer}>
          <button style={modal.cancelBtn} onClick={onClose} disabled={saving}>Cancel</button>
          <button style={{ ...modal.createBtn, ...(saving ? modal.btnDisabled : {}) }}
            onClick={handleSubmit} disabled={saving}>
            {saving ? "Creating…" : "Create client →"}
          </button>
        </div>
      </div>
    </div>
  );
}

const modal = {
  overlay:   { position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 },
  box:       { background: "var(--color-surface)", borderRadius: "var(--radius-xl)", padding: "var(--space-8)", width: 480, maxWidth: "calc(100vw - 48px)", display: "flex", flexDirection: "column", gap: "var(--space-4)" },
  header:    { display: "flex", alignItems: "center", justifyContent: "space-between" },
  title:     { fontSize: "var(--text-lg)", fontWeight: "var(--weight-semibold)", color: "var(--color-text-primary)" },
  close:     { background: "none", border: "none", fontSize: 22, color: "var(--color-text-tertiary)", cursor: "pointer", lineHeight: 1, padding: 0 },
  label:     { display: "flex", flexDirection: "column", gap: "var(--space-1)", fontSize: "var(--text-sm)", color: "var(--color-text-secondary)" },
  req:       { fontSize: 10, fontWeight: "var(--weight-semibold)", textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-critical)", display: "inline-block", marginLeft: "var(--space-2)" },
  input:     { fontSize: "var(--text-sm)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", padding: "var(--space-2) var(--space-3)", color: "var(--color-text-primary)", background: "var(--color-bg-base)", width: "100%", boxSizing: "border-box" },
  row:       { display: "flex", gap: "var(--space-3)" },
  footer:    { display: "flex", justifyContent: "flex-end", gap: "var(--space-3)", paddingTop: "var(--space-2)" },
  error:     { fontSize: "var(--text-sm)", color: "var(--color-critical)", background: "var(--color-critical-bg)", borderRadius: "var(--radius-sm)", padding: "var(--space-2) var(--space-3)" },
  cancelBtn: { fontSize: "var(--text-sm)", background: "none", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-4)", color: "var(--color-text-secondary)", cursor: "pointer" },
  createBtn: { fontSize: "var(--text-sm)", fontWeight: "var(--weight-medium)", background: "var(--color-accent)", color: "var(--color-text-on-accent)", border: "none", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-5)", cursor: "pointer" },
  deleteBtn: { fontSize: "var(--text-sm)", fontWeight: "var(--weight-medium)", background: "var(--color-critical)", color: "#fff", border: "none", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-5)", cursor: "pointer" },
  btnDisabled: { opacity: 0.5, cursor: "not-allowed" },
};
